import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, PackageSearch } from "lucide-react";
import useSeo from "../hooks/useSeo";
import ProductGrid from "../components/product/ProductGrid";
import FilterPanel from "../components/shop/FilterPanel";
import SortDropdown from "../components/shop/SortDropdown";
import EmptyState from "../components/common/EmptyState";
import { products, priceBounds, COLLECTIONS } from "../data/products";

const { max: MAX_PRICE } = priceBounds();

const emptyFilters = () => ({
  collections: [],
  sizes: [],
  colors: [],
  maxPrice: MAX_PRICE,
});

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(emptyFilters);
  const [sort, setSort] = useState("featured");
  const [mobileOpen, setMobileOpen] = useState(false);

  useSeo({
    title: "Shop All",
    description:
      "Shop premium T-shirts from 9'One Store — oversized, essentials, graphics and minimal styles. Filter by collection, size, colour and price.",
    path: "/shop",
  });

  // Prefill collection filter from URL (?collection=oversized).
  useEffect(() => {
    const col = searchParams.get("collection");
    if (col && COLLECTIONS.some((c) => c.slug === col)) {
      setFilters((f) => ({ ...f, collections: [col] }));
    }
  }, [searchParams]);

  const filtered = useMemo(() => {
    let list = products.filter((p) => {
      if (filters.collections.length && !filters.collections.includes(p.collection))
        return false;
      if (filters.sizes.length && !filters.sizes.some((s) => p.sizes.includes(s)))
        return false;
      if (
        filters.colors.length &&
        !filters.colors.some((c) => p.colors.map((x) => x.name).includes(c))
      )
        return false;
      if (p.price > filters.maxPrice) return false;
      return true;
    });

    list = [...list];
    switch (sort) {
      case "price-asc":
        list.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        list.sort((a, b) => b.price - a.price);
        break;
      case "newest":
        list.sort((a, b) => Number(b.newArrival) - Number(a.newArrival));
        break;
      default:
        list.sort((a, b) => Number(b.featured) - Number(a.featured));
    }
    return list;
  }, [filters, sort]);

  const activeCount =
    filters.collections.length +
    filters.sizes.length +
    filters.colors.length +
    (filters.maxPrice < MAX_PRICE ? 1 : 0);

  const clearAll = () => {
    setFilters(emptyFilters());
    setSearchParams({});
  };

  return (
    <div className="animate-fade-in">
      {/* Page heading */}
      <div className="border-b border-ink">
        <div className="container-9one py-12">
          <p className="label-mono text-ink/50">The Catalog</p>
          <h1 className="mt-2 font-display text-5xl uppercase tracking-tight sm:text-7xl">
            Shop All
          </h1>
        </div>
      </div>

      <div className="container-9one py-8">
        {/* Toolbar */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <button
            onClick={() => setMobileOpen(true)}
            className="btn-outline py-2.5 lg:hidden"
            data-testid="mobile-filter-btn"
          >
            <SlidersHorizontal size={15} /> Filters {activeCount > 0 && `(${activeCount})`}
          </button>
          <p className="hidden font-mono text-xs uppercase tracking-wider text-ink/50 lg:block" data-testid="result-count">
            {filtered.length} {filtered.length === 1 ? "Product" : "Products"}
          </p>
          <SortDropdown value={sort} onChange={setSort} />
        </div>

        <div className="flex gap-10">
          {/* Desktop sidebar */}
          <aside className="hidden w-64 shrink-0 lg:block" data-testid="filter-sidebar">
            <div className="flex items-center justify-between">
              <span className="label-mono">Filters</span>
              {activeCount > 0 && (
                <button
                  onClick={clearAll}
                  className="font-mono text-xs uppercase text-ink/50 underline hover:text-ink"
                  data-testid="clear-filters-btn"
                >
                  Clear
                </button>
              )}
            </div>
            <FilterPanel filters={filters} setFilters={setFilters} />
          </aside>

          {/* Grid */}
          <div className="flex-1">
            {filtered.length === 0 ? (
              <EmptyState
                icon={PackageSearch}
                title="No products found"
                message="Try adjusting or clearing your filters."
                testId="shop-empty"
              />
            ) : (
              <ProductGrid products={filtered} />
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[85] lg:hidden" data-testid="mobile-filter-drawer">
          <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm animate-fade-in" onClick={() => setMobileOpen(false)} />
          <div className="absolute right-0 top-0 flex h-full w-[85%] max-w-sm flex-col bg-paper animate-slide-in-right">
            <div className="flex items-center justify-between border-b border-ink px-5 py-4">
              <h2 className="font-display text-2xl uppercase tracking-tight">Filters</h2>
              <button onClick={() => setMobileOpen(false)} aria-label="Close filters" data-testid="mobile-filter-close">
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5">
              <FilterPanel filters={filters} setFilters={setFilters} />
            </div>
            <div className="grid grid-cols-2 gap-3 border-t border-ink px-5 py-4">
              <button onClick={clearAll} className="btn-outline">Clear</button>
              <button onClick={() => setMobileOpen(false)} className="btn-solid" data-testid="mobile-filter-apply">
                Show {filtered.length}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;
